import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { AsDiv, TitleXS } from '@/atoms/Text'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { Modal } from '@/molecules/Modal'
import { FeaturedProjectContentCollection, Maybe, ProjectTheme } from '@/types/codegen-contentful'
import { buildAnalyticsLink } from '@/utils/AnalyticsLinkBuilder'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

interface FeaturedProjectGuildButtonGroupProps {
  campaign?: Maybe<string>
  href?: Maybe<string>
  contentCollection?: Maybe<FeaturedProjectContentCollection>
  guildMemberHref?: Maybe<string>
  isSubscribedToPifOrGuild?: boolean
  isGuildMember?: boolean
  sneakPeekUrl?: Maybe<string>
  projectTheme?: Maybe<ProjectTheme>
  showDavidVideo?: boolean
}

export const FeaturedProjectGuildButtonGroup: React.FC<FeaturedProjectGuildButtonGroupProps> = ({
  campaign = 'guild-membership',
  href = '/guild',
  guildMemberHref = '/watch/sound-of-freedom',
  isSubscribedToPifOrGuild,
  isGuildMember,
  contentCollection,
  sneakPeekUrl,
  projectTheme,
  showDavidVideo = false,
}) => {
  const [showModal, setShowModal] = useState(false)
  const { t } = useTranslate('common')
  const { asPath } = useSafeRouter()
  const track = useSafeTrack()
  const guildMember = Boolean(isSubscribedToPifOrGuild || isGuildMember)
  const linkHref = guildMember ? guildMemberHref : href
  const duration = projectTheme?.sneakPeekDuration ?? null

  const analyticsLink = buildAnalyticsLink({
    href: linkHref as string,
    path: asPath,
    campaign: campaign as string,
    linkContext: 'featured-section',
    source: 'featured-section',
  })
  const analyticsSneakPeekLink = buildAnalyticsLink({
    href: sneakPeekUrl as string,
    path: asPath,
    campaign: campaign as string,
    linkContext: 'featured-section',
    source: 'featured-section',
  })

  const video = useMemo(() => {
    const content = contentCollection?.items?.[0]
    return {
      title: content?.title ?? 'Trailer',
      url: content?.source ?? '',
    }
  }, [contentCollection?.items])

  const handleLinkClick = () => {
    track('Featured Project Link Clicked', { href })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault()
    track('Featured Project Watch Trailer Clicked', { href })
    setShowModal(true)
  }

  const showVideo = Boolean(video?.url || sneakPeekUrl)
  const showSneakPeek = !guildMember && sneakPeekUrl
  const primaryIconColor = showDavidVideo ? 'white' : 'core-gray-950'

  return (
    <div className="flex w-full flex-col justify-center gap-4 md:flex-row">
      <LinkButton
        href={analyticsLink}
        className={classNames('h-12 w-full min-w-[220px] justify-center space-x-2 md:w-fit relative', {
          'text-white !bg-gradient-to-tl !from-[#3B60BA] !to-[#FBA349] !border-transparent !shadow-dark-1 hover:!from-[#3B60BA] hover:!to-[#FBA349]':
            showDavidVideo,
        })}
        variant="white"
        onClick={handleLinkClick}
      >
        {showDavidVideo && <div className="absolute inset-0 rounded-[inherit] bg-black/25" aria-hidden="true" />}
        <span className="relative z-10 flex items-center space-x-2">
          <When condition={guildMember}>
            <PlayIcon size={24} color={primaryIconColor} />
            <TitleXS as={AsDiv}>{t('watchNow', 'Watch Now')}</TitleXS>
          </When>
          <When condition={!guildMember}>
            <GuildIcon size={24} color1={primaryIconColor} />
            <TitleXS as={AsDiv}>{t('joinToWatch', 'Join To Watch')}</TitleXS>
          </When>
        </span>
      </LinkButton>
      <When condition={showVideo}>
        <When condition={showSneakPeek}>
          <LinkButton
            href={analyticsSneakPeekLink}
            className="h-12 w-full min-w-[220px] justify-center space-x-2 !border-transparent bg-white/20 backdrop-blur-xl hover:bg-white/35 hover:text-white md:w-fit"
            variant="white"
            onClick={handleLinkClick}
            outline
          >
            <TitleXS>
              {duration
                ? t('watchFirstMinutes', `Watch The First {{ minutes }} Minutes`, {
                    minutes: duration,
                  })
                : t('watchSneakPeek', 'Watch Sneak Peek')}
            </TitleXS>
          </LinkButton>
        </When>
        <When condition={video?.url && !showSneakPeek}>
          <Button
            className="h-12 w-full min-w-[220px] justify-center space-x-2 !border-transparent bg-white/20 backdrop-blur-xl hover:bg-white/35 hover:text-white md:w-fit"
            variant="white"
            onClick={handleClick}
            outline
          >
            <TitleXS>{t('watchTrailer', 'Watch Trailer')}</TitleXS>
          </Button>
        </When>
      </When>
      <When condition={!showVideo}>
        <LinkButton
          href={analyticsLink}
          className="h-12 w-full min-w-[220px] justify-center space-x-2 !border-transparent bg-white/20 backdrop-blur-xl hover:bg-white/35 hover:text-white md:w-fit"
          variant="white"
          onClick={handleLinkClick}
          outline
        >
          <TitleXS>{t('learnMore', 'Learn More')}</TitleXS>
        </LinkButton>
      </When>
      <Modal
        className="z-[99999]"
        panelClassesOverride="inline-block w-3/4 xl:w-full max-w-7xl align-middle shadow-xl rounded-2xl transition-all transform overflow-hidden"
        childWrapperClassName="mt-0"
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      >
        <EmbeddedVideo className="w-full rounded-2xl" {...video} />
      </Modal>
    </div>
  )
}
